import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { useDialog } from "../../../DialogContext";
import CreateTaskTemplateForm from "../../../../pages/LawyerPages/Task/TaskModuleForms/CreateTaskTemplateForm";
import CreateTaskListForm from "../../../../pages/LawyerPages/Task/TaskModuleForms/CreateTaskListForm";
import CreateTaskForm from "../../../../pages/LawyerPages/Task/TaskModuleForms/CreateTaskForm";
import {
  getAllTask,
  getAllTaskList,
  getAllTaskTemplate,
} from "../../../../redux/Apps/Lawyerapp/TaskSlice/TaskSlice";
import { debounce, pageScrollStyle } from "../../../../styles/styles";
import { NoDataOptionField } from "../../../../static/staticdata";
import noOption from "../../../../assets/no-option-found.webp";
import { AddRounded } from "@mui/icons-material";
import { getNestedError } from "./InputFields";
import { Controller } from "react-hook-form";

const getTaskContextDetails = (apiUrl = "") => {
  const normalized = apiUrl?.toLowerCase?.() || "";
  if (normalized.includes("task-template")) {
    return {
      type: "template",
      label: "Task Template",
      noun: "Task template",
      addButtonText: "Add Task template",
      noDataText: "Don't have any task templates yet",
      dialogTitle: "Create Task template",
      FormComponent: CreateTaskTemplateForm,
      apiFunction: "template",
    };
  }
  if (normalized.includes("task-list")) {
    return {
      type: "group",
      label: "Task Group",
      noun: "Task group",
      addButtonText: "Add Task group",
      noDataText: "Don't have any task groups yet",
      dialogTitle: "Create Task group",
      FormComponent: CreateTaskListForm,
      apiFunction: "list",
    };
  }
  if (
    normalized.includes("task/") &&
    !normalized.includes("task-template") &&
    !normalized.includes("task-list")
  ) {
    return {
      type: "task",
      label: "Task",
      noun: "Task",
      addButtonText: "Add Task",
      noDataText: "Don't have any tasks yet",
      dialogTitle: "Create Task",
      FormComponent: CreateTaskForm,
      apiFunction: "task",
    };
  }
  if (normalized.includes("template")) {
    return {
      type: "template",
      label: "Task Template",
      noun: "Task template",
      addButtonText: "Add Task template",
      noDataText: "Don't have any data yet",
      dialogTitle: "Create Task template",
      FormComponent: CreateTaskTemplateForm,
      apiFunction: "template",
    };
  }
  return {
    type: "template",
    label: "Task",
    noun: "Task",
    addButtonText: "Add Task",
    noDataText: "Don't have any data yet",
    dialogTitle: "Create Task",
    FormComponent: CreateTaskForm,
    apiFunction: "template",
  };
};

const getNextPageNumber = (url) => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const page = parsed.searchParams.get("page");
    return page ? parseInt(page, 10) : null;
  } catch (error) {
    return null;
  }
};

const mergeUniqueOptions = (existing = [], incoming = []) => {
  const map = new Map();
  [...existing, ...incoming].forEach((item) => {
    if (!item) return;
    const key = item.id?.toString() || JSON.stringify(item);
    map.set(key, item);
  });
  return Array.from(map.values());
};

const parseTaskResults = (result) => {
  if (!result) return [];
  if (Array.isArray(result)) return result;
  if (Array.isArray(result.results)) return result.results;
  if (Array.isArray(result.data)) return result.data;
  if (result.results && !Array.isArray(result.results)) {
    return [result.results];
  }
  if (typeof result === "object" && result.id) {
    return [result];
  }
  return [];
};

const TaskApiField = ({
  name,
  label: labelProp,
  control,
  value,
  onChange,
  errors = {},
  token,
  apiUrl,
  placeholder,
  multiple = false,
  returnFullData = false,
  disabled = false,
  searchKey = "search",
  teamMemberLawyerId = null,
  renderNoOptions,
  TaskTypeFormComponent,
  createTaskTypeDialogTitle,
  addButtonText,
  noDataText,
  noDataButtonText,
  noDataImage = noOption,
  optionRenderer,
  getOptionLabel: getOptionLabelProp,
  refreshKey = 0,
  idKey = "id",
  valueKey = null,
  filters = {},
}) => {
  const dispatch = useDispatch();
  const { showDialog } = useDialog();
  const [apiOptions, setApiOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [nextUrl, setNextUrl] = useState(null);
  const [localValue, setLocalValue] = useState(multiple ? [] : null);
  const [isCreating, setIsCreating] = useState(false);
  const fetchAbortController = useRef(null);
  const lastFetchTime = useRef(0);
  const lastSearchText = useRef("");
  const isMounted = useRef(true);

  const context = useMemo(() => getTaskContextDetails(apiUrl), [apiUrl]);
  const effectiveLabel = labelProp || context.label;
  const placeholderText =
    placeholder ||
    (multiple
      ? `Select ${context.label.toLowerCase()}s`
      : `Select ${context.label.toLowerCase()}`);
  const FinalDialogComponent = TaskTypeFormComponent || context.FormComponent;
  const dialogTitle = createTaskTypeDialogTitle || context.dialogTitle;
  const finalAddButtonText = addButtonText || context.addButtonText;
  const finalNoDataText = noDataText || context.noDataText;
  const finalNoDataButtonText =
    noDataButtonText || context.noun || context.label;
  const canCreate = Boolean(FinalDialogComponent) && !disabled;
  const effectiveValueKey = valueKey || idKey;

  const getOptionLabel = useCallback(
    (option) => {
      if (!option) return "";
      if (getOptionLabelProp) return getOptionLabelProp(option);
      return (
        option?.task_name ||
        option?.template_name ||
        option?.task_list_name ||
        option?.name ||
        option?.title ||
        option?.label ||
        `Item ${option?.id || ""}`
      );
    },
    [getOptionLabelProp]
  );

  const isOptionEqualToValue = useCallback(
    (option, val) => {
      if (!option || !val) return false;
      const optionId = option[idKey];
      if (typeof val === "object") {
        const valueId = val[effectiveValueKey];
        return optionId === valueId;
      }
      return optionId === val;
    },
    [idKey, effectiveValueKey]
  );

  const fetchTasks = useCallback(
    async (searchQuery = "", isLoadMore = false) => {
      const now = Date.now();
      if (
        !isLoadMore &&
        now - lastFetchTime.current < 300 &&
        lastSearchText.current === searchQuery
      ) {
        return;
      }

      if (fetchAbortController.current) {
        fetchAbortController.current.abort();
      }

      if (!apiUrl || !token) {
        setApiOptions([]);
        return;
      }

      if (isLoadMore && !nextUrl) return;

      fetchAbortController.current = new AbortController();
      setIsLoading(true);
      lastSearchText.current = searchQuery;
      lastFetchTime.current = now;

      try {
        const page = isLoadMore ? getNextPageNumber(nextUrl) : 1;
        const baseParams = {
          url: apiUrl,
          token,
          page,
          search: searchQuery || undefined,
          searchKey,
          teamMemberLawyerId,
          filters: { ...filters },
          signal: fetchAbortController.current.signal,
        };

        let result;
        switch (context.apiFunction) {
          case "template":
            result = await dispatch(getAllTaskTemplate(baseParams)).unwrap();
            break;
          case "list":
            result = await dispatch(getAllTaskList(baseParams)).unwrap();
            break;
          case "task":
            result = await dispatch(getAllTask(baseParams)).unwrap();
            break;
          default:
            result = await dispatch(getAllTaskTemplate(baseParams)).unwrap();
        }

        if (!isMounted.current) return;

        const parsedOptions = parseTaskResults(result);
        setApiOptions((prev) =>
          mergeUniqueOptions(isLoadMore ? prev : [], parsedOptions)
        );

        const nextPageUrl = result?.next || null;
        setNextUrl(nextPageUrl);
      } catch (error) {
        console.error("Fetch tasks error:", error);
        if (!isLoadMore && isMounted.current) {
          setApiOptions([]);
        }
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    },
    [
      apiUrl,
      token,
      nextUrl,
      dispatch,
      context.apiFunction,
      searchKey,
      teamMemberLawyerId,
      filters,
    ]
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((query) => {
        fetchTasks(query, false);
      }, 400),
    [fetchTasks]
  );

  useEffect(() => {
    if (!apiUrl || !token) return;
    isMounted.current = true;
    const timer = setTimeout(() => {
      fetchTasks("", false);
    }, 100);
    return () => {
      clearTimeout(timer);
      if (fetchAbortController.current) {
        fetchAbortController.current.abort();
      }
    };
  }, [apiUrl, token, refreshKey, fetchTasks]);

  useEffect(() => {
    if (control) return;
    const updateLocalValue = () => {
      if (multiple) {
        if (Array.isArray(value)) {
          const mappedValues = value
            .map((val) => {
              if (typeof val === "object" && val[idKey] !== undefined) {
                return (
                  apiOptions.find((opt) => opt[idKey] === val[idKey]) || val
                );
              }
              return apiOptions.find((opt) => opt[idKey] === val);
            })
            .filter(Boolean);
          setLocalValue(mappedValues);
        } else {
          setLocalValue([]);
        }
      } else {
        if (!value) {
          setLocalValue(null);
          return;
        }
        if (typeof value === "object" && value[idKey] !== undefined) {
          const found = apiOptions.find((opt) => opt[idKey] === value[idKey]);
          setLocalValue(found || value);
        } else {
          const found = apiOptions.find((opt) => opt[idKey] === value);
          setLocalValue(found || null);
        }
      }
    };
    updateLocalValue();
  }, [value, apiOptions, multiple, control, idKey]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (fetchAbortController.current) {
        fetchAbortController.current.abort();
      }
    };
  }, []);

  const handleCreateTask = useCallback(() => {
    if (!canCreate || isCreating) return;
    setIsCreating(true);

    const handleSuccess = () => {
      setIsCreating(false);
      fetchTasks(searchText, false);
    };

    const handleClose = () => {
      setIsCreating(false);
    };

    showDialog(<FinalDialogComponent onSuccess={handleSuccess} />, {
      dialogTitle,
      onClose: handleClose,
    });
  }, [
    FinalDialogComponent,
    canCreate,
    dialogTitle,
    showDialog,
    fetchTasks,
    searchText,
    isCreating,
  ]);

  const noOptionsContent = useMemo(() => {
    if (renderNoOptions) {
      if (typeof renderNoOptions === "function") {
        return renderNoOptions({ handleCreate: handleCreateTask, canCreate });
      }
      return renderNoOptions;
    }
    return (
      <NoDataOptionField
        image={noDataImage}
        text={finalNoDataText}
        buttonText={finalNoDataButtonText}
        showButton={canCreate}
        onClick={handleCreateTask}
      />
    );
  }, [
    renderNoOptions,
    noDataImage,
    finalNoDataText,
    finalNoDataButtonText,
    canCreate,
    handleCreateTask,
  ]);

  const handleScroll = (event) => {
    const node = event?.currentTarget;
    if (!node || isLoading || !nextUrl) return;
    if (node.scrollTop + node.clientHeight >= node.scrollHeight - 50) {
      fetchTasks(searchText, true);
    }
  };

  const handleInputChange = (_, value, reason) => {
    if (reason === "input") {
      setSearchText(value);
      debouncedSearch(value);
    } else if (reason === "clear") {
      setSearchText("");
      fetchTasks("", false);
    }
  };

  const handleSelectionChange = (newValue, fieldOnChange) => {
    let formattedValue;
    if (multiple) {
      formattedValue = returnFullData
        ? newValue || []
        : (newValue || []).map((item) => item?.[idKey]).filter(Boolean);
    } else {
      formattedValue = returnFullData
        ? newValue || null
        : newValue?.[idKey] || null;
    }

    if (!control) {
      setLocalValue(newValue);
    }

    if (fieldOnChange && typeof fieldOnChange === "function") {
      fieldOnChange(formattedValue);
    }
    if (onChange && typeof onChange === "function") {
      onChange(formattedValue);
    }

    if (!multiple && !returnFullData) {
      setSearchText("");
    }
  };

  const renderOption = useCallback(
    (props, option) => {
      const { key, ...otherProps } = props;
      const title = getOptionLabel(option);

      if (typeof optionRenderer === "function") {
        return optionRenderer(props, option, getOptionLabel);
      }

      return (
        <Box component="li" key={key} {...otherProps}>
          <Typography variant="body2">{title}</Typography>
        </Box>
      );
    },
    [optionRenderer, getOptionLabel]
  );

  const fieldError = getNestedError(errors, name);

  const ListboxComponentWithAction = useMemo(
    () =>
      React.forwardRef(function TaskListboxComponent(props, ref) {
        const { children, ...other } = props;
        return (
          <Box
            ref={ref}
            {...other}
            sx={{ ...pageScrollStyle }}
            onScroll={handleScroll}
          >
            {children}
            {canCreate && (
              <Box
                sx={{
                  p: 1,
                  borderTop: 1,
                  borderColor: "divider",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
                onMouseDown={(e) => e.preventDefault()}
              >
                <Button
                  startIcon={<AddRounded />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCreateTask();
                  }}
                  disabled={isCreating}
                  size="small"
                >
                  {finalAddButtonText}
                </Button>
              </Box>
            )}
          </Box>
        );
      }),
    [canCreate, handleCreateTask, finalAddButtonText, isCreating, handleScroll]
  );

  const commonAutocompleteProps = {
    multiple,
    fullWidth: true,
    options: apiOptions,
    inputValue: searchText,
    onInputChange: handleInputChange,
    loading: isLoading,
    disabled: disabled || isCreating,
    getOptionLabel,
    isOptionEqualToValue,
    ListboxComponent: ListboxComponentWithAction,
    noOptionsText: noOptionsContent,
    renderOption,
    disableCloseOnSelect: multiple,
    renderTags: multiple
      ? (tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              label={getOptionLabel(option)}
              {...getTagProps({ index })}
              disabled={disabled || isCreating}
              size="small"
            />
          ))
      : undefined,
    renderInput: (params) => (
      <TextField
        {...params}
        label={
          <>
            {effectiveLabel}
            {fieldError?.type === "required" && (
              <Typography component="span" color="error">
                {" "}
                *
              </Typography>
            )}
          </>
        }
        placeholder={placeholderText}
        size="small"
        fullWidth
        error={Boolean(fieldError)}
        helperText={fieldError?.message}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {isLoading && <CircularProgress color="inherit" size={20} />}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
      />
    ),
  };

  if (control && name) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const displayValue = multiple
            ? (field.value || [])
                .map((val) => {
                  if (
                    typeof val === "object" &&
                    val[effectiveValueKey] !== undefined
                  ) {
                    return (
                      apiOptions.find(
                        (opt) => opt[idKey] === val[effectiveValueKey]
                      ) || val
                    );
                  }
                  return apiOptions.find((opt) => opt[idKey] === val);
                })
                .filter(Boolean)
            : field.value
            ? typeof field.value === "object"
              ? apiOptions.find(
                  (opt) => opt[idKey] === field.value[effectiveValueKey]
                ) || field.value
              : apiOptions.find((opt) => opt[idKey] === field.value)
            : null;

          return (
            <Autocomplete
              {...commonAutocompleteProps}
              value={displayValue}
              onChange={(_, newValue) =>
                handleSelectionChange(newValue, field.onChange)
              }
              onBlur={field.onBlur}
            />
          );
        }}
      />
    );
  }

  return (
    <Autocomplete
      {...commonAutocompleteProps}
      value={localValue}
      onChange={(_, newValue) => handleSelectionChange(newValue)}
    />
  );
};

export default React.memo(TaskApiField);
